import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import ShareButton from "../components/ShareButton";
import { useCurrency } from "../lib/currency";
import { MapPin, ShieldCheck, ArrowLeft, Briefcase, Globe, DollarSign, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function PublicUserPage() {
  const { id } = useParams();
  const { format } = useCurrency();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/public/users/${id}`)
      .then(r => setUser(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="py-20 text-center text-slate-500">Loading profile...</div>;
  if (notFound || !user) return (
    <div className="max-w-md mx-auto py-20 px-6 text-center">
      <h1 className="text-2xl font-bold mb-2">Profile not available</h1>
      <p className="text-slate-500 mb-6">This profile may be private or doesn't exist.</p>
      <Link to="/" className="dv-btn dv-btn-primary">Back to home</Link>
    </div>
  );

  const isInvestor = user.role === "investor";
  const isFounder = user.role === "founder";
  const displayName = user.full_name || user.company_name || `User ${user.id.slice(0, 6)}`;

  return (
    <div data-testid="public-user-page" className="bg-white">
      <Seo
        title={`${displayName} — ${user.role?.charAt(0).toUpperCase() + user.role?.slice(1)} on DealVest`}
        description={user.bio || `${displayName} is on DealVest — the marketplace for business acquisitions and growth capital across Africa and the Middle East.`}
        path={`/u/${user.id}`}
        type="profile"
      />

      <section className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-blue-600 mb-6 text-sm font-medium">
          <ArrowLeft size={14} /> DealVest
        </Link>

        <div className="bg-gradient-to-br from-blue-50 to-white rounded-3xl border border-slate-200 p-8 md:p-12">
          <div className="flex items-start gap-5 mb-6 flex-wrap">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-blue-600 text-white flex items-center justify-center text-3xl font-bold">
              {displayName.slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="dv-pill capitalize">{user.role}</span>
                {user.verification_status === "verified" && (
                  <span className="dv-pill bg-emerald-50 text-emerald-700"><ShieldCheck size={12} /> Verified</span>
                )}
                {user.investor_type && <span className="dv-pill bg-slate-100 text-slate-700">{user.investor_type}</span>}
              </div>
              <h1 className="dv-display text-3xl md:text-4xl text-slate-900">{displayName}</h1>
              {user.country && <p className="text-slate-500 flex items-center gap-1.5 mt-2"><MapPin size={14} /> {user.country}</p>}
            </div>
            <div className="ml-auto">
              <ShareButton url={`/u/${user.id}`} title={`${displayName} on DealVest`} text={user.bio || ""} />
            </div>
          </div>

          {user.bio && (
            <div className="mt-6 mb-2">
              <p className="text-slate-700 text-lg leading-relaxed">{user.bio}</p>
            </div>
          )}
        </div>

        {/* Investor / Founder details */}
        {(isInvestor || isFounder) && (user.industries?.length || user.countries?.length || user.budget_min) && (
          <section className="mt-8">
            <h2 className="font-bold text-2xl mb-5">{isInvestor ? "Investment focus" : "Looking for"}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {user.industries?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <div className="flex items-center gap-2 mb-3 text-sm text-slate-500"><Briefcase size={14} /> Industries</div>
                  <div className="flex flex-wrap gap-2">
                    {user.industries.map((ind) => <span key={ind} className="dv-pill">{ind}</span>)}
                  </div>
                </div>
              )}
              {user.countries?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <div className="flex items-center gap-2 mb-3 text-sm text-slate-500"><Globe size={14} /> Regions</div>
                  <div className="flex flex-wrap gap-2">
                    {user.countries.map((c) => <span key={c} className="dv-pill">{c}</span>)}
                  </div>
                </div>
              )}
              {(user.budget_min || user.budget_max) && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <div className="flex items-center gap-2 mb-3 text-sm text-slate-500"><DollarSign size={14} /> {isInvestor ? "Ticket size" : "Capital needed"}</div>
                  <div className="text-xl font-bold text-slate-900">{format(user.budget_min || 0)} — {format(user.budget_max || 0)}</div>
                </div>
              )}
              {user.deal_types?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <div className="flex items-center gap-2 mb-3 text-sm text-slate-500"><Building2 size={14} /> Deal types</div>
                  <div className="flex flex-wrap gap-2">
                    {user.deal_types.map((d) => <span key={d} className="dv-pill">{d}</span>)}
                  </div>
                </div>
              )}
              {user.business_stage && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <div className="text-sm text-slate-500 mb-2">Business stage</div>
                  <div className="font-semibold text-slate-900">{user.business_stage}</div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="mt-12 rounded-3xl bg-blue-600 text-white p-8 md:p-10 text-center">
          <h3 className="dv-display text-2xl md:text-3xl mb-3">Want to connect with {displayName.split(" ")[0]}?</h3>
          <p className="text-blue-100 mb-6">Sign up to send a message or share an opportunity.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup" className="dv-btn bg-white text-blue-600 hover:bg-blue-50" data-testid="public-signup-cta">Sign up free</Link>
            <Link to="/contact" className="dv-btn bg-white/10 hover:bg-white/20 text-white border border-white/25">Contact DealVest</Link>
          </div>
        </section>
      </section>
    </div>
  );
}
